import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const ASSET_TYPES = ['Laptop', 'Desktop', 'Mobile', 'Tablet', 'Monitor', 'Keyboard', 'Mouse', 'Other'];
const PAGE_SIZE = 10;

const emptyForm = {
  assetName: '',
  assetType: 'Laptop',
  serialNumber: '',
  description: '',
};

const STATUS_CONFIG = {
  available: { color: '#166534', bg: '#dcfce7', dot: '#22c55e', label: 'Available' },
  assigned: { color: '#1e40af', bg: '#dbeafe', dot: '#3b82f6', label: 'Assigned' },
  maintenance: { color: '#92400e', bg: '#fef3c7', dot: '#f59e0b', label: 'Maintenance' },
  retired: { color: '#991b1b', bg: '#fee2e2', dot: '#ef4444', label: 'Retired' },
};

const TYPE_ICONS = {
  Laptop: 'M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8zM6 1v3M10 1v3M14 1v3',
  Desktop: 'M8 21h8M12 17v4M3 8h18M21 5H3a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z',
  Mobile: 'M12 18h.01M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z',
  Monitor: 'M8 21h8M12 17v4M3 3h18v14H3zM',
  Keyboard: 'M9 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-4M9 11h6',
  Mouse: 'M20 9V7a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M12 9h8M16 5V3',
  Tablet: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z',
  Other: 'M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM12 12h.01',
};

function Assets() {
  const { hasPermission, user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Add modal
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [savingEdit, setSavingEdit] = useState(false);

  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [savingAssign, setSavingAssign] = useState(false);

  // View History modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewAsset, setViewAsset] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Maintenance modal
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceTarget, setMaintenanceTarget] = useState(null);
  const [maintenanceForm, setMaintenanceForm] = useState({
    issue: '',
    shopName: '',
    contactPerson: '',
    phoneNumber: '',
    address: '',
    sentOnDate: new Date().toISOString().split('T')[0]
  });
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  // Kebab action menu
  const [actionMenu, setActionMenu] = useState(null); // { id, top, left }
  const [statusDropdown, setStatusDropdown] = useState(null); // { id, top, left }

  // Custom asset types
  const [customTypes, setCustomTypes] = useState([]);
  const [addingType, setAddingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingTypeEdit, setAddingTypeEdit] = useState(false);
  const [newTypeNameEdit, setNewTypeNameEdit] = useState('');

  // Filters
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [showTypeFilterDd, setShowTypeFilterDd] = useState(false);
  const [showEmpFilterDd, setShowEmpFilterDd] = useState(false);
  const [showStatusFilterDd, setShowStatusFilterDd] = useState(false);
  const [typeFilterSearch, setTypeFilterSearch] = useState("");
  const [empFilterSearch, setEmpFilterSearch] = useState("");

  const [showEmpDd, setShowEmpDd] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  const canCreate = hasPermission('assets', 'create');
  const canUpdate = hasPermission('assets', 'update');
  const canDelete = hasPermission('assets', 'delete');

  useEffect(() => {
    fetchAssets();
    if (canUpdate || canCreate) {
      fetchEmployees();
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menus on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('[data-asset-menu]')) setActionMenu(null);
      if (!e.target.closest('[data-status-dropdown]')) setStatusDropdown(null);
      if (!e.target.closest('[data-type-filter]')) setShowTypeFilterDd(false);
      if (!e.target.closest('[data-emp-filter]')) setShowEmpFilterDd(false);
      if (!e.target.closest('[data-status-filter]')) setShowStatusFilterDd(false);
      if (!e.target.closest('[data-emp-select]')) setShowEmpDd(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/assets');
      setAssets(res.data);
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const refetchAssets = async () => {
    setRefetching(true);
    try {
      const res = await api.get('assets');
      setAssets(res.data);
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setRefetching(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('employees');
      setEmployees(res.data);

      // Extract unique departments from employees
      const predefinedDepts = ["IT", "Sales", "HR/Manager"];
      const extractedDepts = res.data.map((emp) => emp.department).filter(Boolean);
      const uniqueDepts = [...new Set([...predefinedDepts, ...extractedDepts])];
      setDepartments(uniqueDepts.sort());
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('roles/list');
      setRoles(res.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  // ---------- Search & Pagination ----------
  const filteredAssets = assets.filter((asset) => {
    const searchMatch = (
      asset.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assetId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assetType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (asset.assignedTo && (asset.assignedTo.firstName + ' ' + asset.assignedTo.lastName).toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const typeMatch = filterType === "" || asset.assetType === filterType;
    const empMatch = filterEmployee === "" || (asset.assignedTo && asset.assignedTo._id === filterEmployee);
    const statusMatch = filterStatus === "" || asset.status === filterStatus;

    let dateMatch = true;
    if (filterDate) {
      if (!asset.assignedDate) {
        dateMatch = false;
      } else {
        const assigned = new Date(asset.assignedDate).setHours(0, 0, 0, 0);
        const filter = new Date(filterDate).setHours(0, 0, 0, 0);
        if (assigned !== filter) dateMatch = false;
      }
    }

    return searchMatch && typeMatch && empMatch && statusMatch && dateMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredAssets.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paginated = filteredAssets.slice(pageStart, pageStart + PAGE_SIZE);

  // ---------- Stats ----------
  const stats = {
    total: assets.length,
    available: assets.filter((a) => a.status === 'available').length,
    assigned: assets.filter((a) => a.status === 'assigned').length,
    maintenance: assets.filter((a) => a.status === 'maintenance').length,
  };

  // ---------- Action menu ----------
  const toggleMenu = (e, id) => {
    e.stopPropagation();
    if (actionMenu?.id === id) { setActionMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 180; // approximate dropdown height
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 6;
    setActionMenu({ id, top, left: rect.left - 130 });
  };

  const toggleStatusDropdown = (e, id) => {
    e.stopPropagation();
    if (statusDropdown?.id === id) { setStatusDropdown(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150; 
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 6;
    setStatusDropdown({ id, top, left: rect.left });
  };

  // ---------- Add ----------
  const generateAssetId = () => `AST-${Date.now().toString(36).toUpperCase()}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('assets', { ...formData, assetId: generateAssetId() });
      setShowModal(false);
      setFormData(emptyForm);
      setAddingType(false);
      setNewTypeName('');
      refetchAssets();
    } catch {
      alert('Error creating asset');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Edit ----------
  const openEdit = (asset) => {
    setEditAsset(asset);
    setEditForm({
      assetName: asset.assetName,
      assetType: asset.assetType,
      serialNumber: asset.serialNumber || '',
      description: asset.description || '',
    });
    setAddingTypeEdit(false);
    setNewTypeNameEdit('');
    setShowEditModal(true);
    setActionMenu(null);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`assets/${editAsset._id}`, editForm);
      setShowEditModal(false);
      refetchAssets();
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating asset');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- Assign ----------
  const openAssign = (asset) => {
    setAssignTarget(asset);
    setSelectedEmployee(asset.assignedTo?._id || '');
    setEmpSearch('');
    setShowEmpDd(false);
    setShowAssignModal(true);
    setActionMenu(null);
  };

  const openView = async (asset) => {
    setActionMenu(null);
    setShowViewModal(true);
    setLoadingHistory(true);
    try {
      // Use relative path so it correctly appends to /api baseURL
      const res = await api.get(`assets/${asset._id}`);
      setViewAsset(res.data);
    } catch (err) {
      console.error('Error fetching asset details:', err);
      // Fallback: show local data if fetch fails
      setViewAsset(asset);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    setSavingAssign(true);
    try {
      await api.put(`assets/${assignTarget._id}`, { assignedTo: selectedEmployee || null });
      setShowAssignModal(false);
      refetchAssets();
    } catch {
      alert('Error assigning asset');
    } finally {
      setSavingAssign(false);
    }
  };

  const handleUnassign = async (asset) => {
    setActionMenu(null);
    try {
      await api.put(`assets/${asset._id}`, { assignedTo: null });
      refetchAssets();
    } catch {
      alert('Error unassigning asset');
    }
  };

  const handleMaintenance = (asset) => {
    setActionMenu(null);
    setMaintenanceTarget(asset);
    setMaintenanceForm({
      issue: '',
      shopName: '',
      contactPerson: '',
      phoneNumber: '',
      address: '',
      sentOnDate: new Date().toISOString().split('T')[0]
    });
    setShowMaintenanceModal(true);
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    setSavingMaintenance(true);
    try {
      const dateString = new Date().toLocaleString();
      const updatedByName = user ? `${user.firstName} ${user.lastName} (${dateString})` : `Unknown (${dateString})`;
      const payload = {
        status: 'maintenance',
        assignedTo: null,
        maintenanceDetails: {
          ...maintenanceForm,
          updatedBy: updatedByName
        }
      };
      await api.put(`assets/${maintenanceTarget._id}`, payload);
      setShowMaintenanceModal(false);
      setMaintenanceTarget(null);
      refetchAssets();
    } catch {
      alert('Error saving maintenance details');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const handleStatusChange = async (asset, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (newStatus !== 'assigned') payload.assignedTo = null;
      await api.put(`assets/${asset._id}`, payload);
      refetchAssets();
    } catch {
      alert('Error updating asset status');
    }
  };

  const handleStatusSelect = (asset, newStatus) => {
    setStatusDropdown(null);
    if (newStatus === 'assigned') {
      openAssign(asset);
    } else if (newStatus === 'maintenance') {
      handleMaintenance(asset);
    } else if (newStatus !== asset.status) {
      handleStatusChange(asset, newStatus);
    }
  };

  // ---------- Delete ----------
  const handleDelete = async (asset) => {
    setActionMenu(null);
    if (!window.confirm(`Delete asset "${asset.assetName}"?`)) return;
    try {
      await api.delete(`assets/${asset._id}`);
      refetchAssets();
    } catch {
      alert('Error deleting asset');
    }
  };

  // ---------- Skeleton ----------
  if (loading || refetching) {
    return (
      <div
        className="page-container"
        style={{
          minHeight: '100vh',
          background: '#f8fafc',
          padding: '28px 28px 40px',
          fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
        }}
      >
        <style>{`
          @keyframes assets-shimmer {
            0% { background-position: -600px 0; }
            100% { background-position: 600px 0; }
          }
          .assets-skeleton {
            border-radius: 8px;
            background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
            background-size: 600px 100%;
            animation: assets-shimmer 1.4s infinite linear;
          }
        `}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '22px' }}>
          <div>
            <div className="assets-skeleton" style={{ width: 200, height: 28, marginBottom: 8 }} />
            <div className="assets-skeleton" style={{ width: 300, height: 16 }} />
          </div>
          <div className="assets-skeleton" style={{ width: 110, height: 36, borderRadius: 10 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px' }}>
              <div className="assets-skeleton" style={{ width: 80, height: 13, marginBottom: 10 }} />
              <div className="assets-skeleton" style={{ width: 48, height: 28, marginBottom: 12 }} />
              <div className="assets-skeleton" style={{ width: 34, height: 3 }} />
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
            <div className="assets-skeleton" style={{ width: 240, height: 36, borderRadius: 10 }} />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '10px 16px', borderBottom: '1px solid #f8fafc' }}>
              <div className="assets-skeleton" style={{ width: 80, height: 14 }} />
              <div className="assets-skeleton" style={{ flex: 1, height: 14 }} />
              <div className="assets-skeleton" style={{ width: 70, height: 14 }} />
              <div className="assets-skeleton" style={{ width: 60, height: 20, borderRadius: 999 }} />
              <div className="assets-skeleton" style={{ width: 100, height: 14 }} />
              <div className="assets-skeleton" style={{ width: 28, height: 28, borderRadius: 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Main UI ----------
  return (
    <div
      className="page-container"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#f8fafc',
        padding: '28px 28px 40px',
        fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <style>{`
        @keyframes assets-bubble-float-large {
          0% { transform: translate(0,0) scale(1); }
          30% { transform: translate(-5px,-7px) scale(1.04); }
          60% { transform: translate(4px,-4px) scale(0.97); }
          100% { transform: translate(0,0) scale(1); }
        }
        @keyframes assets-bubble-float-small {
          0% { transform: translate(0,0) scale(1); }
          35% { transform: translate(6px,-9px) scale(1.06); }
          65% { transform: translate(-4px,-3px) scale(0.95); }
          100% { transform: translate(0,0) scale(1); }
        }
        .assets-metrics-grid > div {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .assets-metrics-grid > div::before {
          content: '';
          position: absolute;
          z-index: 0;
          width: 90px; height: 90px;
          border-radius: 50%;
          opacity: 0.06;
          right: -26px; bottom: -26px;
          pointer-events: none;
          animation: assets-bubble-float-large 5.5s ease-in-out infinite;
        }
        .assets-metrics-grid > div::after {
          content: '';
          position: absolute;
          z-index: 0;
          width: 54px; height: 54px;
          border-radius: 50%;
          opacity: 0.04;
          right: 36px; bottom: -20px;
          pointer-events: none;
          animation: assets-bubble-float-small 4.2s ease-in-out infinite;
        }
        .assets-metrics-grid > div:nth-child(1)::before,
        .assets-metrics-grid > div:nth-child(1)::after { background: #667eea; }
        .assets-metrics-grid > div:nth-child(2)::before,
        .assets-metrics-grid > div:nth-child(2)::after { background: #10b981; }
        .assets-metrics-grid > div:nth-child(3)::before,
        .assets-metrics-grid > div:nth-child(3)::after { background: #3b82f6; }
        .assets-metrics-grid > div:nth-child(4)::before,
        .assets-metrics-grid > div:nth-child(4)::after { background: #f59e0b; }
        .assets-metrics-grid > div:nth-child(2)::before { animation-delay: 0.8s; }
        .assets-metrics-grid > div:nth-child(3)::before { animation-delay: 1.5s; }
        .assets-metrics-grid > div:nth-child(4)::before { animation-delay: 2.2s; }
        .assets-metrics-grid > div:nth-child(2)::after { animation-delay: 1.2s; }
        .assets-metrics-grid > div:nth-child(3)::after { animation-delay: 0.5s; }
        .assets-metrics-grid > div:nth-child(4)::after { animation-delay: 1.8s; }

        .asset-menu-btn {
          transition: background 0.15s ease;
        }
        .asset-menu-btn:hover {
          background: #f8fafc !important;
        }
        .asset-menu-btn-danger:hover {
          background: #fff5f5 !important;
        }
        .asset-menu-btn-warn:hover {
          background: #fffbeb !important;
        }
        @media (max-width: 768px) {
          .asset-form-grid {
            grid-template-columns: 1fr !important;
          }
          @keyframes empDdIn {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: '#0f172a', margin: 0, letterSpacing: '-0.025em' }}>
            Asset Management
          </h1>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0', fontWeight: 500 }}>
            {canUpdate || canCreate ? 'Manage and assign company assets to employees.' : 'Assets assigned to you are listed here.'}
          </p>
        </div>
        {(canCreate || canUpdate) && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 14px', border: 'none', borderRadius: 10,
              background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)',
              color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Asset
          </button>
        )}
      </div>

      {/* ── Stats ── */}
      {(canUpdate || canCreate) && (
        <div
          className="assets-metrics-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 22 }}
        >
          {[
            { label: 'Total Assets', value: stats.total, color: '#667eea', filter: '' },
            { label: 'Available', value: stats.available, color: '#10b981', filter: 'available' },
            { label: 'Assigned', value: stats.assigned, color: '#3b82f6', filter: 'assigned' },
            { label: 'Under Maintenance', value: stats.maintenance, color: '#f59e0b', filter: 'maintenance' },
          ].map((item) => (
            <div
              key={item.label}
              onClick={() => { setFilterStatus(item.filter); setCurrentPage(1); }}
              style={{
                background: '#fff', border: filterStatus === item.filter ? `1.5px solid ${item.color}` : '1px solid #e2e8f0',
                borderRadius: 12, padding: '16px 18px', cursor: 'pointer',
                boxShadow: filterStatus === item.filter ? `0 4px 14px ${item.color}20` : 'none',
                transform: filterStatus === item.filter ? 'translateY(-2px)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 10 }}>{item.label}</div>
                <div style={{ fontSize: 28, fontWeight: 300, color: '#0f172a', lineHeight: 1 }}>{item.value}</div>
                <div style={{ width: 34, height: 3, borderRadius: 999, background: item.color, marginTop: 12, opacity: 0.8 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Table card ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
        {/* search bar */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', minWidth: 240, maxWidth: 340, width: '100%' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search assets by name, ID, type..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif" }}
            />
          </div>

          {(canUpdate || canCreate) && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {/* Type Filter */}
              <div style={{ position: 'relative' }} data-type-filter>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTypeFilterDd(!showTypeFilterDd); setShowEmpFilterDd(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '999px',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                    background: showTypeFilterDd ? '#f0fdf4' : '#ffffff',
                    color: '#475569', outline: 'none',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{filterType === "" ? "All Types" : filterType}</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showTypeFilterDd ? 'rotate(180deg)' : 'none' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {showTypeFilterDd && (
                  <div onClick={e => e.stopPropagation()} style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                    background: '#ffffff', borderRadius: '14px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 300,
                    width: '240px', padding: '10px', overflow: 'hidden',
                    border: '1px solid #f1f5f9',
                    animation: 'empDdIn 0.2s ease'
                  }}>
                    <input
                      type="text"
                      placeholder="Search types..."
                      value={typeFilterSearch}
                      onChange={(e) => setTypeFilterSearch(e.target.value)}
                      style={{
                        width: '100%', padding: '7px 10px', border: '1px solid #f1f5f9',
                        borderRadius: '8px', fontSize: '11px', background: '#f8fafc',
                        outline: 'none', marginBottom: '8px'
                      }}
                    />
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <div
                        onClick={() => { setFilterType(""); setShowTypeFilterDd(false); setTypeFilterSearch(""); setCurrentPage(1); }}
                        style={{
                          padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                          fontWeight: filterType === "" ? '700' : '500',
                          cursor: 'pointer', color: filterType === "" ? '#15803d' : '#1e293b',
                          background: filterType === "" ? '#dcfce7' : 'transparent',
                          marginBottom: '2px'
                        }}
                      >All Types</div>
                      {Object.keys(TYPE_ICONS).filter(t => t.toLowerCase().includes(typeFilterSearch.toLowerCase())).map(type => (
                        <div
                          key={type}
                          onClick={() => { setFilterType(type); setShowTypeFilterDd(false); setTypeFilterSearch(""); setCurrentPage(1); }}
                          style={{
                            padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                            fontWeight: filterType === type ? '700' : '500',
                            cursor: 'pointer', color: filterType === type ? '#15803d' : '#1e293b',
                            background: filterType === type ? '#dcfce7' : 'transparent',
                            marginBottom: '2px'
                          }}
                        >{type}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Employee Filter */}
              <div style={{ position: 'relative' }} data-emp-filter>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowEmpFilterDd(!showEmpFilterDd); setShowTypeFilterDd(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '999px',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                    background: showEmpFilterDd ? '#f0fdf4' : '#ffffff',
                    color: '#475569', outline: 'none',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{filterEmployee === "" ? "All Employees" : employees.find(e => e._id === filterEmployee)?.firstName + ' ' + employees.find(e => e._id === filterEmployee)?.lastName || "All Employees"}</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showEmpFilterDd ? 'rotate(180deg)' : 'none' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {showEmpFilterDd && (
                  <div onClick={e => e.stopPropagation()} style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                    background: '#ffffff', borderRadius: '14px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 300,
                    width: '240px', padding: '10px', overflow: 'hidden',
                    border: '1px solid #f1f5f9',
                    animation: 'empDdIn 0.2s ease'
                  }}>
                    <input
                      type="text"
                      placeholder="Search employees..."
                      value={empFilterSearch}
                      onChange={(e) => setEmpFilterSearch(e.target.value)}
                      style={{
                        width: '100%', padding: '7px 10px', border: '1px solid #f1f5f9',
                        borderRadius: '8px', fontSize: '11px', background: '#f8fafc',
                        outline: 'none', marginBottom: '8px'
                      }}
                    />
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <div
                        onClick={() => { setFilterEmployee(""); setShowEmpFilterDd(false); setEmpFilterSearch(""); setCurrentPage(1); }}
                        style={{
                          padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                          fontWeight: filterEmployee === "" ? '700' : '500',
                          cursor: 'pointer', color: filterEmployee === "" ? '#15803d' : '#1e293b',
                          background: filterEmployee === "" ? '#dcfce7' : 'transparent',
                          marginBottom: '2px'
                        }}
                      >All Employees</div>
                      {employees.filter(emp => (emp.firstName + ' ' + emp.lastName).toLowerCase().includes(empFilterSearch.toLowerCase())).map(emp => (
                        <div
                          key={emp._id}
                          onClick={() => { setFilterEmployee(emp._id); setShowEmpFilterDd(false); setEmpFilterSearch(""); setCurrentPage(1); }}
                          style={{
                            padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                            fontWeight: filterEmployee === emp._id ? '700' : '500',
                            cursor: 'pointer', color: filterEmployee === emp._id ? '#15803d' : '#1e293b',
                            background: filterEmployee === emp._id ? '#dcfce7' : 'transparent',
                            marginBottom: '2px'
                          }}
                        >{emp.firstName} {emp.lastName}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Filter */}
              <div style={{ position: 'relative' }} data-status-filter>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowStatusFilterDd(!showStatusFilterDd); setShowTypeFilterDd(false); setShowEmpFilterDd(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '999px',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                    background: showStatusFilterDd ? '#f0fdf4' : '#ffffff',
                    color: '#475569', outline: 'none',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{filterStatus === "" ? "All Status" : STATUS_CONFIG[filterStatus]?.label || "All Status"}</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showStatusFilterDd ? 'rotate(180deg)' : 'none' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {showStatusFilterDd && (
                  <div onClick={e => e.stopPropagation()} style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                    background: '#ffffff', borderRadius: '14px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 300,
                    width: '180px', padding: '10px', overflow: 'hidden',
                    border: '1px solid #f1f5f9',
                    animation: 'empDdIn 0.2s ease'
                  }}>
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <div
                        onClick={() => { setFilterStatus(""); setShowStatusFilterDd(false); setCurrentPage(1); }}
                        style={{
                          padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                          fontWeight: filterStatus === "" ? '700' : '500',
                          cursor: 'pointer', color: filterStatus === "" ? '#15803d' : '#1e293b',
                          background: filterStatus === "" ? '#dcfce7' : 'transparent',
                          marginBottom: '2px'
                        }}
                      >All Status</div>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <div
                          key={key}
                          onClick={() => { setFilterStatus(key); setShowStatusFilterDd(false); setCurrentPage(1); }}
                          style={{
                            padding: '7px 10px', borderRadius: '6px', fontSize: '11px',
                            fontWeight: filterStatus === key ? '700' : '500',
                            cursor: 'pointer', color: filterStatus === key ? '#15803d' : '#1e293b',
                            background: filterStatus === key ? '#dcfce7' : 'transparent',
                            marginBottom: '2px',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: config.dot, flexShrink: 0 }} />
                          {config.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '999px',
                      fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                      background: filterDate ? '#f0fdf4' : '#ffffff',
                      color: filterDate ? '#15803d' : '#475569', outline: 'none',
                      fontFamily: "'Inter', sans-serif",
                      transition: 'all 0.2s',
                    }}
                  >
                    <span>
                      {filterDate
                        ? new Date(filterDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : "Select Date"}
                    </span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </button>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => { setFilterDate(e.target.value); setCurrentPage(1); }}
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      opacity: 0, cursor: 'pointer'
                    }}
                    title="Select Date"
                  />
                </div>
                {filterDate && (
                  <button onClick={() => { setFilterDate(""); setCurrentPage(1); }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginLeft: 'auto' }}>
            Showing {filteredAssets.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + paginated.length, filteredAssets.length)} of {filteredAssets.length}
          </div>
        </div>

        <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {/* empty state */}
        {filteredAssets.length === 0 ? (
          <div style={{ padding: '64px 24px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No assets found</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {canCreate || canUpdate ? 'Add your first asset using the button above.' : 'No assets have been assigned to you yet.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr style={{ background: '#f8fafc' }}>
                  {['S.No', 'Asset', 'Type', 'Serial Number', 'Status', (canUpdate || canDelete) && 'Assigned To', (canUpdate || canDelete) && 'Assigned Date', (canUpdate || canDelete) && 'Actions'].filter(Boolean).map((label) => (
                    <th
                      key={label}
                      style={{ padding: '9px 12px', textAlign: label === 'Actions' ? 'center' : 'left', fontSize: '9.5px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((asset, idx) => {
                  const sc = STATUS_CONFIG[asset.status] || STATUS_CONFIG.available;
                  return (
                    <tr key={asset._id} style={{ borderBottom: idx < paginated.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                      {/* S.No */}
                      <td style={{ padding: '9px 12px', fontSize: '11px', color: '#475569', fontWeight: 600 }}>
                        {pageStart + idx + 1}
                      </td>
                      {/* Asset name + ID */}
                      <td style={{ padding: '9px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d={TYPE_ICONS[asset.assetType] || TYPE_ICONS.Other} />
                            </svg>
                          </div>
                          <div>
                            <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>{asset.assetName}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{asset.assetId}</div>
                          </div>
                        </div>
                      </td>
                      {/* Type */}
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: asset.assetType === 'Laptop' ? '#1e40af' : asset.assetType === 'Mobile' ? '#166534' : asset.assetType === 'Tablet' ? '#86198f' : '#854d0e', background: asset.assetType === 'Laptop' ? '#dbeafe' : asset.assetType === 'Mobile' ? '#dcfce3' : asset.assetType === 'Tablet' ? '#fae8ff' : '#fef3c7' }}>
                          {asset.assetType}
                        </span>
                      </td>
                      {/* Serial */}
                      <td style={{ padding: '9px 12px', fontSize: '11px', color: '#64748b' }}>{asset.serialNumber || '—'}</td>
                      {/* Status badge */}
                      <td style={{ padding: '9px 12px' }}>
                        {canUpdate ? (
                          <button
                            onClick={(e) => toggleStatusDropdown(e, asset._id)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 12px 3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: sc.color, background: sc.bg, border: `1px solid ${sc.color}40`, cursor: 'pointer', outline: 'none'
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                            {sc.label}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }}><polyline points="6 9 12 15 18 9" /></svg>
                          </button>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: sc.color, background: sc.bg }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                            {sc.label}
                          </span>
                        )}
                      </td>
                      {/* Admin/Manager columns */}
                      {(canUpdate || canDelete) && (
                        <>
                          <td style={{ padding: '9px 12px' }}>
                            {asset.assignedTo ? (
                              <>
                                <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                                  {asset.assignedTo.firstName} {asset.assignedTo.lastName}
                                </div>
                                <div style={{ fontSize: 10, color: '#94a3b8' }}>{asset.assignedTo.employeeId}</div>
                              </>
                            ) : (
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '9px 12px', fontSize: 10, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {asset.assignedDate ? new Date(asset.assignedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }} data-asset-menu>
                              <button
                                onClick={() => openView(asset)}
                                title="View History"
                                style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6366f1' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              {canUpdate && (
                                <button
                                  onClick={() => openEdit(asset)}
                                  title="Edit"
                                  style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#2563eb' }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                  </svg>
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDelete(asset)}
                                  title="Delete"
                                  style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dc2626' }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={(e) => toggleMenu(e, asset._id)}
                                title="More actions"
                                style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569', boxShadow: actionMenu?.id === asset._id ? '0 8px 18px rgba(15,23,42,0.08)' : 'none' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredAssets.length > 0 && totalPages > 1 && (
          <div style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            paddingBottom: '16px'
          }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '7px',
                background: safePage === 1 ? '#f0fdf4' : '#a3e635',
                border: 'none',
                cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                outline: 'none',
                opacity: safePage === 1 ? 0.4 : 1
              }}
              onMouseEnter={(e) => {
                if (safePage !== 1) {
                  e.currentTarget.style.background = '#84cc16';
                }
              }}
              onMouseLeave={(e) => {
                if (safePage !== 1) {
                  e.currentTarget.style.background = '#a3e635';
                }
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#365314" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Page Numbers */}
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (safePage <= 3) {
                pageNum = i + 1;
              } else if (safePage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = safePage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '7px',
                    background: safePage === pageNum ? '#a3e635' : '#f0fdf4',
                    border: safePage === pageNum ? 'none' : '1px solid #d9f99d',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: safePage === pageNum ? '#365314' : '#4d7c0f',
                    fontFamily: "'Inter', -apple-system, sans-serif"
                  }}
                  onMouseEnter={(e) => {
                    if (safePage !== pageNum) {
                      e.currentTarget.style.background = '#bef264';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (safePage !== pageNum) {
                      e.currentTarget.style.background = '#f0fdf4';
                    }
                  }}
                >
                  {pageNum}
                </button>
              );
            })}

            {/* Ellipsis if more pages */}
            {totalPages > 5 && safePage < totalPages - 2 && (
              <span style={{
                padding: '0 4px',
                color: '#84cc16',
                fontSize: '12px',
                fontWeight: '700',
                fontFamily: "'Inter', -apple-system, sans-serif"
              }}>...</span>
            )}

            {/* Last page if needed */}
            {totalPages > 5 && safePage < totalPages - 2 && (
              <button
                onClick={() => setCurrentPage(totalPages)}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '7px',
                  background: '#f0fdf4',
                  border: '1px solid #d9f99d',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#4d7c0f',
                  fontFamily: "'Inter', -apple-system, sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#bef264';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f0fdf4';
                }}
              >
                {totalPages}
              </button>
            )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '7px',
                background: safePage === totalPages ? '#f0fdf4' : '#a3e635',
                border: 'none',
                cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                outline: 'none',
                opacity: safePage === totalPages ? 0.4 : 1
              }}
              onMouseEnter={(e) => {
                if (safePage !== totalPages) {
                  e.currentTarget.style.background = '#84cc16';
                }
              }}
              onMouseLeave={(e) => {
                if (safePage !== totalPages) {
                  e.currentTarget.style.background = '#a3e635';
                }
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#365314" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
        </div>
      </div>

      {/* ── Kebab dropdown menu ── */}
      {actionMenu && (() => {
        const activeAsset = assets.find((a) => a._id === actionMenu.id);
        if (!activeAsset) return null;
        return (
          <div
            data-asset-menu
            style={{ position: 'fixed', top: actionMenu.top, left: actionMenu.left, width: 160, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', padding: 6, zIndex: 1200, fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", fontWeight: 400 }}
          >
            {canUpdate && (
              <button onClick={() => openAssign(activeAsset)} className="asset-menu-btn" style={{ width: '100%', border: 'none', background: 'transparent', borderRadius: 8, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 400, color: '#334155', cursor: 'pointer', textAlign: 'left' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                Assign
              </button>
            )}
            {canUpdate && activeAsset.status === 'assigned' && (
              <button onClick={() => handleUnassign(activeAsset)} className="asset-menu-btn" style={{ width: '100%', border: 'none', background: 'transparent', borderRadius: 8, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 400, color: '#334155', cursor: 'pointer', textAlign: 'left' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Unassign
              </button>
            )}
            {canUpdate && activeAsset.status !== 'maintenance' && (
              <button onClick={() => handleMaintenance(activeAsset)} className="asset-menu-btn asset-menu-btn-warn" style={{ width: '100%', border: 'none', background: 'transparent', borderRadius: 8, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 400, fontStyle: 'normal', color: '#d4a017', cursor: 'pointer', textAlign: 'left', WebkitFontSmoothing: 'antialiased' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M4.93 4.93a10 10 0 0 0 0 14.14" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="M2 12h2" /><path d="M20 12h2" />
                </svg>
                Under Maintenance
              </button>
            )}
            <button onClick={() => openView(activeAsset)} className="asset-menu-btn" style={{ width: '100%', border: 'none', background: 'transparent', borderRadius: 8, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 400, color: '#334155', cursor: 'pointer', textAlign: 'left' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
              </svg>
              View History
            </button>
          </div>
        );
      })()}

      {/* ── Status Dropdown Menu ── */}
      {statusDropdown && (() => {
        const activeAsset = assets.find((a) => a._id === statusDropdown.id);
        if (!activeAsset) return null;
        return (
          <div
            data-status-dropdown
            style={{ position: 'fixed', top: statusDropdown.top, left: statusDropdown.left, width: 140, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', padding: 6, zIndex: 1200, fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", fontWeight: 400 }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', padding: '6px 8px', fontStyle: 'italic', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>Select Status</div>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={() => handleStatusSelect(activeAsset, key)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: activeAsset.status === key ? config.bg : 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: activeAsset.status === key ? 700 : 600, color: activeAsset.status === key ? config.color : '#475569', transition: 'background 0.2s', textAlign: 'left' }}
                onMouseEnter={(e) => { if(activeAsset.status !== key) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if(activeAsset.status !== key) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: config.dot }} />
                {config.label}
              </button>
            ))}
          </div>
        );
      })()}

      {/* ── Add Asset Modal ── */}
      {showModal && (
        <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 540, background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 20px 40px rgba(15,23,42,0.18)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>Add Asset</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Register a new company asset.</div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="asset-form-grid" style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
                {/* Asset Name */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  <span>Asset Name <span style={{ color: '#ef4444' }}>*</span></span>
                  <input type="text" required placeholder='e.g. MacBook Pro 14"' value={formData.assetName} onChange={(e) => setFormData({ ...formData, assetName: e.target.value })}
                    style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
                {/* Asset Type with Add type */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  <span>Asset Type <span style={{ color: '#ef4444' }}>*</span></span>
                  {addingType ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input autoFocus type="text" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="Enter type name"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newTypeName.trim()) { setCustomTypes((p) => [...p, newTypeName.trim()]); setFormData({ ...formData, assetType: newTypeName.trim() }); setNewTypeName(''); setAddingType(false); } } }}
                        style={{ flex: 1, height: 38, borderRadius: 10, border: '1px solid #a3e635', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                      <button type="button" onClick={() => { if (newTypeName.trim()) { setCustomTypes((p) => [...p, newTypeName.trim()]); setFormData({ ...formData, assetType: newTypeName.trim() }); setNewTypeName(''); setAddingType(false); } }}
                        style={{ height: 38, padding: '0 14px', borderRadius: 10, border: 'none', background: '#a3e635', color: '#1a2e05', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add</button>
                      <button type="button" onClick={() => { setAddingType(false); setNewTypeName(''); }}
                        style={{ height: 38, padding: '0 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  ) : (
                    <select value={formData.assetType} onChange={(e) => { if (e.target.value === '__add_new__') { setAddingType(true); } else { setFormData({ ...formData, assetType: e.target.value }); } }}
                      style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none', background: '#fff' }}>
                      {[...ASSET_TYPES, ...customTypes].map((t) => <option key={t} value={t}>{t}</option>)}
                      <option value="__add_new__">＋ Add new type...</option>
                    </select>
                  )}
                </label>
                {/* Serial Number */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Serial Number
                  <input type="text" placeholder="e.g. C02XG0XXJGH5" value={formData.serialNumber} onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
                <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Description
                  <textarea rows={3} placeholder="Optional notes about the asset" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{ borderRadius: 12, border: '1px solid #e2e8f0', padding: '10px 12px', fontSize: 12, color: '#0f172a', resize: 'vertical', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
              </div>
              <div style={{ padding: '16px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="emp-btn-ghost" onClick={() => setShowModal(false)}><span>Cancel</span></button>
                <button type="submit" disabled={saving} style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Creating…' : 'Add Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showEditModal && editAsset && (
        <div onClick={() => setShowEditModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 20px 40px rgba(15,23,42,0.18)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Edit Asset</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Update asset information.</div>
              </div>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="asset-form-grid" style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
                {/* Asset Name */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  <span>Asset Name <span style={{ color: '#ef4444' }}>*</span></span>
                  <input type="text" required placeholder='e.g. MacBook Pro 14"' value={editForm.assetName} onChange={(e) => setEditForm({ ...editForm, assetName: e.target.value })}
                    style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
                {/* Asset ID - read-only */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Asset ID
                  <div style={{ height: 38, borderRadius: 10, border: '1px solid #f1f5f9', padding: '0 12px', fontSize: 12, color: '#64748b', background: '#f8fafc', display: 'flex', alignItems: 'center', letterSpacing: '0.04em', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif" }}>
                    {editAsset?.assetId}
                  </div>
                </label>
                {/* Asset Type with Add type */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  <span>Asset Type <span style={{ color: '#ef4444' }}>*</span></span>
                  {addingTypeEdit ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input autoFocus type="text" value={newTypeNameEdit} onChange={(e) => setNewTypeNameEdit(e.target.value)} placeholder="Enter type name"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newTypeNameEdit.trim()) { setCustomTypes((p) => [...p, newTypeNameEdit.trim()]); setEditForm({ ...editForm, assetType: newTypeNameEdit.trim() }); setNewTypeNameEdit(''); setAddingTypeEdit(false); } } }}
                        style={{ flex: 1, height: 38, borderRadius: 10, border: '1px solid #a3e635', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                      <button type="button" onClick={() => { if (newTypeNameEdit.trim()) { setCustomTypes((p) => [...p, newTypeNameEdit.trim()]); setEditForm({ ...editForm, assetType: newTypeNameEdit.trim() }); setNewTypeNameEdit(''); setAddingTypeEdit(false); } }}
                        style={{ height: 38, padding: '0 14px', borderRadius: 10, border: 'none', background: '#a3e635', color: '#1a2e05', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add</button>
                      <button type="button" onClick={() => { setAddingTypeEdit(false); setNewTypeNameEdit(''); }}
                        style={{ height: 38, padding: '0 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  ) : (
                    <select value={editForm.assetType} onChange={(e) => { if (e.target.value === '__add_new__') { setAddingTypeEdit(true); } else { setEditForm({ ...editForm, assetType: e.target.value }); } }}
                      style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none', background: '#fff' }}>
                      {[...ASSET_TYPES, ...customTypes].map((t) => <option key={t} value={t}>{t}</option>)}
                      <option value="__add_new__">＋ Add new type...</option>
                    </select>
                  )}
                </label>
                {/* Serial Number */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Serial Number
                  <input type="text" placeholder="e.g. C02XG0XXJGH5" value={editForm.serialNumber} onChange={(e) => setEditForm({ ...editForm, serialNumber: e.target.value })}
                    style={{ height: 38, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 12, color: '#0f172a', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
                <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Description
                  <textarea rows={3} placeholder="Optional notes about the asset" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    style={{ borderRadius: 12, border: '1px solid #e2e8f0', padding: '10px 12px', fontSize: 12, color: '#0f172a', resize: 'vertical', fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif", outline: 'none' }} />
                </label>
              </div>
              <div style={{ padding: '16px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="emp-btn-ghost" onClick={() => setShowEditModal(false)}><span>Cancel</span></button>
                <button type="submit" disabled={savingEdit} style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.6 : 1 }}>
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Assign Modal ── */}
      {showAssignModal && assignTarget && (
        <div onClick={() => setShowAssignModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 20px 40px rgba(15,23,42,0.18)', overflow: 'visible' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Assign Asset</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{assignTarget.assetName} · {assignTarget.assetId}</div>
              </div>
              <button onClick={() => setShowAssignModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleAssignSubmit} style={{ overflow: 'visible' }}>
              <div style={{ padding: '20px 22px', overflow: 'visible' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  Assign To Employee
                  <div style={{ position: 'relative' }} data-emp-select>
                    <div
                      onClick={() => setShowEmpDd(!showEmpDd)}
                      style={{
                        height: 40, borderRadius: 10, border: '1px solid #e2e8f0',
                        padding: '0 12px', fontSize: 12, color: '#0f172a',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#fff', cursor: 'pointer', transition: 'all 0.2s',
                        borderColor: showEmpDd ? '#3b82f6' : '#e2e8f0',
                        boxShadow: showEmpDd ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                      }}
                    >
                      <span style={{ color: selectedEmployee ? '#0f172a' : '#94a3b8', fontWeight: selectedEmployee ? 600 : 400 }}>
                        {selectedEmployee
                          ? (() => {
                            const emp = employees.find(e => e._id === selectedEmployee);
                            return emp ? `${emp.firstName} ${emp.lastName} (${emp.employeeId})` : 'Select Employee';
                          })()
                          : '— Unassigned —'}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showEmpDd ? 'rotate(180deg)' : 'none' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>

                    {showEmpDd && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 1100,
                        overflow: 'hidden', animation: 'empDdIn 0.2s ease'
                      }}>
                        <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search employees..."
                            value={empSearch}
                            onChange={(e) => setEmpSearch(e.target.value)}
                            style={{
                              width: '100%', height: 34, borderRadius: 8, border: '1px solid #f1f5f9',
                              padding: '0 10px', fontSize: 12, background: '#f8fafc', outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          <div
                            onClick={() => { setSelectedEmployee(""); setShowEmpDd(false); }}
                            style={{
                              padding: '10px 12px', fontSize: 12, cursor: 'pointer',
                              color: '#64748b', background: selectedEmployee === "" ? '#f1f5f9' : 'transparent',
                              fontWeight: selectedEmployee === "" ? 700 : 400
                            }}
                          >— Unassigned —</div>
                          {employees.filter(e => {
                            const q = empSearch.toLowerCase();
                            const matchesSearch = `${e.firstName} ${e.lastName} ${e.employeeId}`.toLowerCase().includes(q);
                            // Only show active employees
                            return matchesSearch && e.isActive !== false;
                          }).map((emp) => (
                            <div
                              key={emp._id}
                              onClick={() => { setSelectedEmployee(emp._id); setShowEmpDd(false); }}
                              style={{
                                padding: '10px 12px', fontSize: 12, cursor: 'pointer',
                                display: 'flex', flexDirection: 'column', gap: 2,
                                color: selectedEmployee === emp._id ? '#1e293b' : '#334155',
                                background: selectedEmployee === emp._id ? '#f1f5f9' : 'transparent',
                                borderLeft: selectedEmployee === emp._id ? '3px solid #3b82f6' : '3px solid transparent'
                              }}
                            >
                              <div style={{ fontWeight: 600 }}>{emp.firstName} {emp.lastName}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>{emp.employeeId}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <div style={{ padding: '16px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="emp-btn-ghost" onClick={() => setShowAssignModal(false)}><span>Cancel</span></button>
                <button type="submit" disabled={savingAssign} style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: savingAssign ? 'not-allowed' : 'pointer', opacity: savingAssign ? 0.6 : 1 }}>
                  {savingAssign ? 'Saving…' : 'Assign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Maintenance Details Modal ── */}
      {showMaintenanceModal && (
        <div onClick={() => setShowMaintenanceModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 540, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 25px 50px rgba(15,23,42,0.22)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>Maintenance Details</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Record details for asset {maintenanceTarget?.assetName}</div>
              </div>
              <button onClick={() => setShowMaintenanceModal(false)} style={{ background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: 10, fontSize: 20, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            <form onSubmit={handleMaintenanceSubmit}>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Issue Details <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea required value={maintenanceForm.issue} onChange={e => setMaintenanceForm({ ...maintenanceForm, issue: e.target.value })} style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, minHeight: 80, outline: 'none', fontFamily: 'inherit' }} placeholder="Describe the problem..." />
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Shop Name</label>
                    <input type="text" required value={maintenanceForm.shopName} onChange={e => setMaintenanceForm({ ...maintenanceForm, shopName: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} placeholder="Enter Service Center" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Sent On Date</label>
                    <input type="date" required value={maintenanceForm.sentOnDate} onChange={e => setMaintenanceForm({ ...maintenanceForm, sentOnDate: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Contact Person</label>
                    <input type="text" value={maintenanceForm.contactPerson} onChange={e => setMaintenanceForm({ ...maintenanceForm, contactPerson: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} placeholder="Enter Name" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Phone Number</label>
                    <input type="tel" value={maintenanceForm.phoneNumber} onChange={e => { const val = e.target.value.replace(/\D/g, ''); setMaintenanceForm({ ...maintenanceForm, phoneNumber: val }); }} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} placeholder="Enter Phone" />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Shop Address</label>
                  <textarea value={maintenanceForm.address} onChange={e => setMaintenanceForm({ ...maintenanceForm, address: e.target.value })} style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, minHeight: 60, outline: 'none', fontFamily: 'inherit' }} placeholder="Full address..." />
                </div>
              </div>
              <div style={{ padding: '16px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa' }}>
                <button type="button" onClick={() => setShowMaintenanceModal(false)} style={{ padding: '9px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={savingMaintenance} style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: savingMaintenance ? 'not-allowed' : 'pointer', opacity: savingMaintenance ? 0.6 : 1 }}>
                  {savingMaintenance ? 'Saving…' : 'Send to Maintenance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Details/History Modal ── */}
      {showViewModal && (
        <div onClick={() => setShowViewModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 580, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 25px 50px rgba(15,23,42,0.22)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>Asset Lifecycle & History</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Track ownership and status changes over time.</div>
              </div>
              <button onClick={() => setShowViewModal(false)} style={{ background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: 10, fontSize: 20, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 40, height: 40, border: '3px solid #f1f5f9', borderTopColor: '#3b775e', borderRadius: '50%', animation: 'assets-spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                  <div style={{ fontSize: 13, color: '#64748b' }}>Loading history...</div>
                </div>
              ) : viewAsset ? (
                <>
                  {/* Core Info Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28, background: '#f8fafc', padding: '16px', borderRadius: 14, border: '1px solid #f1f5f9' }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Asset Name</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{viewAsset.assetName}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Asset ID</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{viewAsset.assetId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_CONFIG[viewAsset.status]?.dot || '#94a3b8' }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: STATUS_CONFIG[viewAsset.status]?.color || '#64748b' }}>{STATUS_CONFIG[viewAsset.status]?.label || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Maintenance Details */}
                  {viewAsset.maintenanceDetails && (
                    <div style={{ marginBottom: 28 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5">
                          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                        </svg>
                        Maintenance Details
                      </div>
                      <div style={{ padding: '16px', borderRadius: 12, border: '1px solid #fef3c7', background: '#fffbeb' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Issue</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.issue}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Shop Name</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.shopName}</div>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Contact Person</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.contactPerson || '-'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Phone Number</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.phoneNumber || '-'}</div>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Sent On Date</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                              {viewAsset.maintenanceDetails.sentOnDate ? new Date(viewAsset.maintenanceDetails.sentOnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Updated By</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.updatedBy}</div>
                          </div>
                        </div>
                        {viewAsset.maintenanceDetails.address && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Shop Address</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{viewAsset.maintenanceDetails.address}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Current Assignment */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                      Current Ownership
                    </div>
                    {viewAsset.assignedTo ? (
                      <div style={{ padding: '16px', borderRadius: 12, border: '1px solid #e0f2fe', background: '#f0f9ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0369a1' }}>{viewAsset.assignedTo.firstName} {viewAsset.assignedTo.lastName}</div>
                          <div style={{ fontSize: 11, color: '#0ea5e9', fontWeight: 600, marginTop: 2 }}>Employee ID: {viewAsset.assignedTo.employeeId}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: '#0ea5e9', fontWeight: 700, textTransform: 'uppercase' }}>Assigned Since</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginTop: 2 }}>{new Date(viewAsset.assignedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '20px', borderRadius: 12, border: '1px dashed #e2e8f0', background: '#fafafa', textAlign: 'center', color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
                        Currently unassigned and available in inventory.
                      </div>
                    )}
                  </div>

                  {/* History Timeline */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                      Assignment History
                    </div>

                    {(!viewAsset.assignmentHistory || viewAsset.assignmentHistory.length === 0) ? (
                      <div style={{ textAlign: 'center', padding: '30px 0', border: '1px solid #f1f5f9', borderRadius: 12 }}>
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>No previous history recorded for this asset.</div>
                      </div>
                    ) : (
                      <div style={{ position: 'relative', paddingLeft: 24 }}>
                        {/* Vertical line */}
                        <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 2, background: '#f1f5f9', borderRadius: 1 }} />

                        {[...viewAsset.assignmentHistory].map((hist, i) => {
                          const isMaintenance = hist.statusType === 'maintenance';
                          return (
                          <div key={i} style={{ position: 'relative', marginBottom: 20 }}>
                            {/* Dot */}
                            <div style={{ position: 'absolute', left: -24, top: 4, width: 10, height: 10, borderRadius: '50%', background: isMaintenance ? (hist.returnedDate ? '#e2e8f0' : '#f59e0b') : (hist.returnedDate ? '#e2e8f0' : '#22c55e'), border: '2px solid #fff', boxShadow: '0 0 0 2px #fff', zIndex: 1 }} />

                            <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid #f1f5f9', background: isMaintenance ? (hist.returnedDate ? '#fff' : '#fffbeb') : (hist.returnedDate ? '#fff' : '#f0fdf4') }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {isMaintenance ? (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>
                                      Maintenance ({hist.maintenanceDetails?.shopName || 'Unknown Shop'})
                                    </>
                                  ) : (
                                    `${hist.employee?.firstName || 'Unknown'} ${hist.employee?.lastName || 'Employee'}`
                                  )}
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: isMaintenance ? (hist.returnedDate ? '#f8fafc' : '#fef3c7') : (hist.returnedDate ? '#f8fafc' : '#dcfce7'), color: isMaintenance ? (hist.returnedDate ? '#64748b' : '#d97706') : (hist.returnedDate ? '#64748b' : '#16a34a') }}>
                                  {isMaintenance ? (hist.returnedDate ? 'COMPLETED' : 'IN PROGRESS') : (hist.returnedDate ? 'RETURNED' : 'CURRENT')}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 20 }}>
                                <div>
                                  <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{isMaintenance ? 'Sent On' : 'Assigned'}</div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{new Date(hist.assignedDate || hist.maintenanceDetails?.sentOnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                </div>
                                {hist.returnedDate && (
                                  <div>
                                    <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{isMaintenance ? 'Completed On' : 'Returned'}</div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{new Date(hist.returnedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', background: '#fafafa', textAlign: 'right' }}>
              <button onClick={() => setShowViewModal(false)} style={{ padding: '8px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Close Details</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

{/* Animation keyframes */ }
const style = document.createElement('style');
style.textContent = `
  @keyframes assets-spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default Assets;
